// src/pages/Inventory/STNOutwardDirect.jsx
// ✅ Converted from: STNOutward_Direct.aspx + STNOutward_Direct.aspx.cs + INV_STNOutward.cs
// ✅ Uses Node routes: /api/inventory/stn-outward-direct/*
// ✅ NO rights logic (excluded as instructed)
// ✅ ALL business logic preserved:
//    - Auto-generate STN Outward No (storename/year/STNOUT/0001)
//    - From Store: auto-filled + disabled
//    - To Branch dropdown → triggers To Store load
//    - Financial year date boundary validation
//    - Item search (by category, item code, item name with autocomplete)
//    - Future date issue check before adding items to grid
//    - Transfer qty validations (not 0, ≤ available stock, ≤ datewise qty, ≤ adjusted qty minus pending outward)
//    - Serial No entry popup logic (fld_IsSerialNo = true)
//    - Serial No count vs Transfer qty mismatch check
//    - Select All checkboxes on both grids
//    - Duplicate item check before adding to outward list
//    - Submit: InsertSTNOutward_Direct_WOStockUpdate
//    - Edit Submit: UpdateSTNOutward_Direct_WOStockUpdate
//    - Approve flow: update master + detail approval flags
//    - Existing STN load: usp_directSTNO_GetSTNDetails → populate all fields + edit grid
//    - Retry logic (up to 10x) for duplicate outward number
//    - SMS redirect after successful save
// ✅ Pagination added on all 3 grids
// ✅ Edit mode: fields disabled (from store, to branch, to store, date)

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft, ChevronRight, Search, PlusCircle,
  RefreshCcw, CheckSquare, ArrowLeft, Send, Check
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
const getTodayISO = () => new Date().toISOString().slice(0, 10);

function isoToDDMM(v) {
  if (!v) return "";
  const [yyyy, mm, dd] = v.split("-");
  return `${dd}/${mm}/${yyyy}`;
}
function ddmmToISO(v) {
  if (!v) return "";
  if (v.includes("-")) return v;
  const p = v.split("/");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : v;
}
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end   = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button onClick={() => onPage(page-1)} disabled={page===1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronLeft size={15}/>
      </button>
      {start > 1 && (<><button onClick={() => onPage(1)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200">1</button><span className="text-xs px-1">…</span></>)}
      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${p===page?"bg-blue-600 text-white":"hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}
      {end < totalPages && (<><span className="text-xs px-1">…</span><button onClick={() => onPage(totalPages)} className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200">{totalPages}</button></>)}
      <button onClick={() => onPage(page+1)} disabled={page===totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
        <ChevronRight size={15}/>
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function STNOutwardDirect() {
  const navigate    = useNavigate();
  const { theme }   = useContext(ThemeContext);
  const isDark      = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass  = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session values ──
  const branchId    = Number(localStorage.getItem("branchId")  || 0);
  const finYearId   = Number(localStorage.getItem("finYearId") || 0);
  const loginUserId = localStorage.getItem("userId") || "";
 
  
  const username    = localStorage.getItem("username")    || "";
  const finYearStart = localStorage.getItem("financeyearstartdate") || "";
  const finYearEnd   = localStorage.getItem("financeyearEnddate")   || "";

  // ── style helpers ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const inputDisabled = `w-full px-3 py-2 rounded-xl border outline-none text-sm opacity-60 cursor-not-allowed ` +
    (isDark ? "bg-gray-700/60 border-white/10 text-gray-400" : "bg-gray-100 border-gray-200 text-gray-500");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark?"text-gray-300":"text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark?"text-gray-300":"text-gray-700"}`;

  // ─────────────────── Edit Mode Detection ───────────────────
  // ASP: Session["stnoutward"] → load existing STN
  const sessionStnOutwardNo  = sessionStorage.getItem("stnoutward")        || "";
  const sessionStnOutwardId  = sessionStorage.getItem("Sessionwordwardid") || "";
  const sessionPurRequestNo  = sessionStorage.getItem("pur_stnrequestno")  || "";
  const isEditMode = !!sessionStnOutwardNo;

  // ─────────────────── Header Form State ───────────────────
  const [stnOutwardNo,    setStnOutwardNo]    = useState(sessionStnOutwardNo || "");
  const [fromStoreId,     setFromStoreId]     = useState("Select");
  const [toBranchId,      setToBranchId]      = useState("Select");
  const [toStoreId,       setToStoreId]       = useState("Select");
  const [currentDate,     setCurrentDate]     = useState("");
  const [remarks,         setRemarks]         = useState("");
  const [approveUserId,   setApproveUserId]   = useState("Select");

  // ─────────────────── Dropdown Data ───────────────────
  const [fromStores,    setFromStores]    = useState([]);
  const [toBranches,    setToBranches]    = useState([]);
  const [toStores,      setToStores]      = useState([]);
  const [itemCategories,setItemCategories]= useState([]);
  const [approveUsers,  setApproveUsers]  = useState([]);

  // ─────────────────── Filter / Search State ───────────────────
  const [itemCategoryId, setItemCategoryId] = useState("Select");
  const [itemCode,       setItemCode]       = useState("");
  const [itemName,       setItemName]       = useState("");
  const [showFilterArea, setShowFilterArea] = useState(!isEditMode);

  // ─────────────────── Autocomplete State ───────────────────
  const [acCodeSuggestions, setAcCodeSuggestions] = useState([]);
  const [showAcCode,        setShowAcCode]         = useState(false);
  const [acNameSuggestions, setAcNameSuggestions] = useState([]);
  const [showAcName,        setShowAcName]         = useState(false);
  const acCodeRef = useRef(null);
  const acNameRef = useRef(null);

  // ─────────────────── Grid 1: Item Search Results ───────────────────
  const [itemSearchRows, setItemSearchRows] = useState([]);
  const [itemSearchPage, setItemSearchPage] = useState(1);
  const ITEM_PAGE_SIZE = 10;
  const itemSearchTotalPages = Math.ceil(itemSearchRows.length / ITEM_PAGE_SIZE);
  const itemSearchPaged = itemSearchRows.slice((itemSearchPage-1)*ITEM_PAGE_SIZE, itemSearchPage*ITEM_PAGE_SIZE);

  // ─────────────────── Grid 2: Outward List (New Entry) ───────────────────
  // Each row: { fld_itemid, fld_branchitemid, fld_itemcode, fld_itemname, fld_itemPartNo,
  //             fld_uomname, fld_closing_stock, datewiseqty, transfered, fld_IsSerialNo,
  //             serialno, fld_IsSelected, transferQty (editable) }
  const [outwardRows,     setOutwardRows]     = useState([]);
  const [outwardPage,     setOutwardPage]     = useState(1);
  const OUTWARD_PAGE_SIZE = 10;
  const outwardTotalPages = Math.ceil(outwardRows.length / OUTWARD_PAGE_SIZE);
  const outwardPaged = outwardRows.slice((outwardPage-1)*OUTWARD_PAGE_SIZE, outwardPage*OUTWARD_PAGE_SIZE);
  const [selectAllOutward, setSelectAllOutward] = useState(false);

  // ─────────────────── Grid 3: Edit List ───────────────────
  const [editRows,      setEditRows]      = useState([]);
  const [editPage,      setEditPage]      = useState(1);
  const EDIT_PAGE_SIZE = 10;
  const editTotalPages = Math.ceil(editRows.length / EDIT_PAGE_SIZE);
  const editPaged = editRows.slice((editPage-1)*EDIT_PAGE_SIZE, editPage*EDIT_PAGE_SIZE);
  const [selectAllApprove, setSelectAllApprove] = useState(false);
  const [showEditGrid,  setShowEditGrid]  = useState(false);
  const [editEnabled,   setEditEnabled]   = useState(true);
  const [grandTotal,    setGrandTotal]    = useState("0");

  // ─────────────────── Warning / list message ───────────────────
  const [futureDateItems, setFutureDateItems] = useState([]);

  // ─────────────────── Loading ───────────────────
  const [loading,       setLoading]       = useState(false);
  const [submitting,    setSubmitting]    = useState(false);

  // ─────────────────── Serial No session (per item) ───────────────────
  // ASP: Session["STNOBranchItemSno" + userid + itemid]
  // In React: stored in component state as a map: { [itemid]: [{ItemSnoId, itemid}] }
  const [serialNoMap, setSerialNoMap] = useState({});

  // ──────────────────────────────────────────────
  // Close autocomplete on outside click
  // ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (acCodeRef.current && !acCodeRef.current.contains(e.target)) setShowAcCode(false);
      if (acNameRef.current && !acNameRef.current.contains(e.target)) setShowAcName(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ══════════════════════════════════════════════════════
  // MOUNT: Load dropdowns + if edit mode, load existing data
  // ASP: Page_Load → ddl_filedata() + fileexistingdata() + fillexistingitemsgrid()
  // ══════════════════════════════════════════════════════
  useEffect(() => {
    loadMeta();
    if (isEditMode) {
      loadExistingData();
    }
    // eslint-disable-next-line
  }, []);

  // ──────────────────────────────────────────────
  // Load all dropdowns
  // ASP: ddl_filedata()
  //   - From Store: usp_GetStoresByBranchid (auto-select index 1, disabled)
  //   - To Branch:  usp_DirectSTNO_GetBranchForDDL (exclude current branch)
  //   - Approve User: usp_GetApprovePersonSTN
  //   - Item Categories: usp_GetItemCategory
  // ──────────────────────────────────────────────
  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/meta`, {
        params: { branchId, finYearId },
      });
      if (res.data?.success) {
        const d = res.data;
        setFromStores(d.fromStores     || []);
        setToBranches(d.toBranches     || []);
        setApproveUsers(d.approveUsers || []);
        setItemCategories(d.itemCategories || []);
        // ASP: ddl_fromstore auto-select index 1 (first real store), disabled
        if (d.fromStores && d.fromStores.length > 0) {
          setFromStoreId(String(d.fromStores[0].value || d.fromStores[0].fld_storeid));
        }
      }
    } catch (e) {
      console.error("meta error:", e.message);
    }
  }, [branchId, finYearId]);

  // ──────────────────────────────────────────────
  // Load existing STN data for edit mode
  // ASP: fileexistingdata() → usp_directSTNO_GetSTNDetails
  // ──────────────────────────────────────────────
  const loadExistingData = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/existing-data`, {
        params: {
          stnOutwardNo: sessionStnOutwardNo,
          branchId,
        },
      });
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        // ASP: ddl_fromstore.SelectedValue = dtcode.Rows[0]["fld_Branchid"]
        setFromStoreId(String(d.fld_Branchid   || "Select"));
        setToBranchId(String(d.fld_toBranchid  || "Select"));
        setCurrentDate(ddmmToISO(d.fld_stn_date_formatted || ""));
        setRemarks(d.fld_remarks || "");
        if (d.fld_Approveperson) {
          setApproveUserId(String(d.fld_Approveperson));
        }
        // load To Store based on existing to branch
        if (d.fld_toBranchid && d.fld_toBranchid !== "Select") {
          await loadToStores(String(d.fld_toBranchid));
        }
        setShowFilterArea(false);
        setShowEditGrid(true);
        // load edit grid items
        await loadEditGridItems();
      }
    } catch (e) {
      toast.error("Failed to load existing STN data: " + e.message);
    }
  }, [sessionStnOutwardNo, branchId]);

  // ──────────────────────────────────────────────
  // Load edit grid items
  // ASP: fillexistingitemsgrid() → usp_DirectSTNO_GetSTNOutExistingItem
  // ──────────────────────────────────────────────
  const loadEditGridItems = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/edit-items`, {
        params: {
          branchId,
          stnOutwardId: sessionStnOutwardId,
        },
      });
      if (res.data?.success) {
        const rows = res.data.items || [];
        // ASP: check fld_ismasapprove → disable grid footer
        const isApproved = rows.length > 0 && rows[0].fld_ismasapprove === "1";
        setEditEnabled(!isApproved);
        // Add approval checkbox state to each row
        const enriched = rows.map(r => ({
          ...r,
          isApproveChecked: r.fld_isapprove === true || r.fld_isapprove === "True",
          updateTransferQty: String(r.fld_TransferQty || ""),
        }));
        setEditRows(enriched);
        setShowEditGrid(true);
        // grand total from DB
        if (res.data.grandTotal) setGrandTotal(res.data.grandTotal);
        // ASP: check current user vs approveperson → show/hide approve column
        if (res.data.approvePerson) {
          // approve column visibility handled in render based on this
          sessionStorage.setItem("stnApprovePersonId", String(res.data.approvePerson));
        }
      }
    } catch (e) {
      toast.error("Failed to load edit items: " + e.message);
    }
  }, [branchId, sessionStnOutwardId]);

  // ──────────────────────────────────────────────
  // Load To Stores based on selected To Branch
  // ASP: ddl_transferbranch_SelectedIndexChanged → usp_DirectSTNO_GetStoresNameByBranchid
  // ──────────────────────────────────────────────
  const loadToStores = useCallback(async (branchVal) => {
    if (!branchVal || branchVal === "Select") {
      setToStores([]);
      setToStoreId("Select");
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/to-stores`, {
        params: { toBranchId: branchVal },
      });
      if (res.data?.success) {
        setToStores(res.data.stores || []);
        setToStoreId("Select");
      }
    } catch (e) {
      console.error("load to stores error:", e.message);
    }
  }, []);

  // ──────────────────────────────────────────────
  // From Store change → reload To Branch
  // ASP: ddl_fromstore_SelectedIndexChanged → usp_FromStoreChangedinSTNOW
  // ──────────────────────────────────────────────
  const handleFromStoreChange = useCallback(async (val) => {
    setFromStoreId(val);
    if (val === "Select") return;
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/branches-for-fromstore`, {
        params: { fromStore: val, branchId },
      });
      if (res.data?.success) {
        setToBranches(res.data.branches || []);
        setToBranchId("Select");
        setToStores([]);
        setToStoreId("Select");
      }
    } catch (e) {
      console.error("from store change error:", e.message);
    }
  }, [branchId]);

  // ──────────────────────────────────────────────
  // To Branch change
  // ASP: ddl_transferbranch_SelectedIndexChanged
  // ──────────────────────────────────────────────
  const handleToBranchChange = useCallback(async (val) => {
    setToBranchId(val);
    await loadToStores(val);
  }, [loadToStores]);

  // ──────────────────────────────────────────────
  // Date validation — financial year boundary
  // ASP: checkDate() + txt_currentdate_TextChanged()
  // ──────────────────────────────────────────────
  const validateDate = useCallback((val) => {
    if (!val) return true;
    const today = getTodayISO();
    if (val > today) {
      toast.warning("You can select only Previous days");
      return false;
    }
    if (finYearStart) {
      const fyISO = ddmmToISO(finYearStart);
      if (val < fyISO) {
        toast.warning(`You can select only Greater than or Equal to Financial Year Date ${finYearStart}`);
        return false;
      }
    }
    if (finYearEnd) {
      const fyEndISO = ddmmToISO(finYearEnd);
      if (val > fyEndISO) {
        toast.warning(`You can select only Less than or Equal to Financial Year End Date ${finYearEnd}`);
        return false;
      }
    }
    return true;
  }, [finYearStart, finYearEnd]);

  const handleDateChange = useCallback((val) => {
    if (!validateDate(val)) return;
    setCurrentDate(val);
  }, [validateDate]);

  // ──────────────────────────────────────────────
  // Autocomplete for item code / item name
  // ASP: AutoCompleteExtender1/2 → GetCompletionList
  // Context key uses: toStoreId, branchId, itemCategoryId
  // ──────────────────────────────────────────────
  const fetchAutoComplete = useCallback(async (field, prefix) => {
    if (!prefix || toStoreId === "Select") return;
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/autocomplete`, {
        params: {
          field,           // "code" or "name"
          prefix,
          toStoreId,
          branchId,
          itemCategoryId: itemCategoryId === "Select" ? "" : itemCategoryId,
        },
      });
      if (res.data?.success) {
        if (field === "code") {
          setAcCodeSuggestions(res.data.suggestions || []);
          setShowAcCode(true);
        } else {
          setAcNameSuggestions(res.data.suggestions || []);
          setShowAcName(true);
        }
      }
    } catch { /* silent */ }
  }, [toStoreId, branchId, itemCategoryId]);

  // ──────────────────────────────────────────────
  // Search items
  // ASP: btn_srch_Click → usp_GetItemsForSTNRequest
  // ──────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (toStoreId === "Select") {
      toast.warning("Please Select the Requested Store");
      return;
    }
    if (toBranchId === "Select") {
      toast.warning("Please select Transfer Branch Field");
      return;
    }
    setLoading(true);
    setItemSearchPage(1);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/search-items`, {
        params: {
          toStoreId,
          fromStoreId,
          itemCategoryId: itemCategoryId === "Select" ? "" : itemCategoryId,
          itemCode: itemCode.trim(),
          itemName: itemName.trim(),
        },
      });
      if (res.data?.success) {
        const rows = (res.data.items || []).map(r => ({ ...r, isSelected: false }));
        setItemSearchRows(rows);
        if (rows.length === 0) toast.info("No Records Found");
      }
    } catch (e) {
      toast.error("Search failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [toStoreId, fromStoreId, itemCategoryId, itemCode, itemName, toBranchId]);

  // ──────────────────────────────────────────────
  // Add selected items from search grid to outward grid
  // ASP: gv_itemmaster_RowCommand("Add") → fillselecteditemsgrid()
  //   → usp_directSTNO_fillselecteditemsgridinSTNOutward
  //   → future date check on ledger
  // ──────────────────────────────────────────────
  const handleAddItems = useCallback(async () => {
    if (!currentDate) {
      toast.warning("Pl enter STN Date and continue");
      return;
    }
    const selectedItems = itemSearchRows.filter(r => r.isSelected);
    
    if (selectedItems.length === 0) {
      toast.warning("Please select any One");
      return;
    }
    // Exclude already-added items
    const existingIds = new Set(outwardRows.map(r => String(r.fld_itemid)));
    const newItems = selectedItems.filter(r => !existingIds.has(String(r.itemid)));
    if (newItems.length === 0) {
      toast.info("All selected items already added");
      return;
    }
    const itemIds = newItems.map(r => r.itemid || r.fld_itemid).join(",");
console.log("🔍 selected itemIds being sent:", itemIds);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/fill-selected-items`, {
  params: {
    itemIds,
    branchId,
    fromStoreId,
    currentDate: isoToDDMM(currentDate),
    finYearId,
  },
});
      if (res.data?.success) {
        const result = res.data;
        // ASP: future date issue warning
        if (result.futureDateItems && result.futureDateItems.length > 0) {
          setFutureDateItems(result.futureDateItems);
        } else {
          setFutureDateItems([]);
        }
        const newRows = (result.items || []).map(r => ({
          ...r,
          transferQty: r.transfered ? String(r.transfered) : "",
          isSelected:  true,
        }));
        if (newRows.length === 0) {
          toast.warning("Items may not be available on the selected date.");
          return;
        }
        // Merge with existing
        setOutwardRows(prev => {
          const existingMap = new Map(prev.map(r => [String(r.fld_itemid), r]));
          newRows.forEach(r => {
            if (!existingMap.has(String(r.fld_itemid))) {
              existingMap.set(String(r.fld_itemid), r);
            }
          });
          return Array.from(existingMap.values());
        });
      }
    } catch (e) {
      toast.error("Failed to load items: " + e.message);
    }
  }, [currentDate, itemSearchRows, outwardRows, branchId, fromStoreId]);

  // ──────────────────────────────────────────────
  // Transfer qty change validation
  // ASP: txt_transferqty_TextChanged
  //   - Not 0
  //   - ≤ available stock (adjusted for pending outwards)
  //   - ≤ datewise qty
  // ──────────────────────────────────────────────
  const handleTransferQtyChange = useCallback(async (itemid, value, rowData) => {
    // Update value in state immediately
    setOutwardRows(prev => prev.map(r =>
      String(r.fld_itemid) === String(itemid) ? { ...r, transferQty: value } : r
    ));
    const qty = parseFloat(value);
    if (isNaN(qty) || qty === 0) {
      toast.warning("Transfer Qty should not be Zero");
      setOutwardRows(prev => prev.map(r =>
        String(r.fld_itemid) === String(itemid) ? { ...r, transferQty: "" } : r
      ));
      return;
    }
    // Check against pending outwards (from server)
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-outward-direct/check-qty`, {
        params: { fromStoreId, itemid, availableQty: rowData.fld_closing_stock },
      });
      if (res.data?.success) {
        const adjustedAvail = res.data.adjustedAvailableQty;
        if (qty > adjustedAvail) {
          toast.warning("Please Enter the Transfer Qty less than or equal to Available Qty");
          setOutwardRows(prev => prev.map(r =>
            String(r.fld_itemid) === String(itemid) ? { ...r, transferQty: "" } : r
          ));
          return;
        }
      }
    } catch { /* silent fallback */ }
    const datewiseQty = parseFloat(rowData.datewiseqty || 0);
    if (qty > datewiseQty) {
      toast.warning("Please Enter the Transfer Qty less than or equal to Datewise Available Stock");
      setOutwardRows(prev => prev.map(r =>
        String(r.fld_itemid) === String(itemid) ? { ...r, transferQty: "" } : r
      ));
    }
  }, [fromStoreId]);

  // ──────────────────────────────────────────────
  // Edit grid transfer qty change
  // ASP: txt_updatetransferqty_TextChanged
  // ──────────────────────────────────────────────
  const handleEditQtyChange = useCallback((itemid, value, rowData) => {
    const qty = parseFloat(value);
    setEditRows(prev => prev.map(r =>
      String(r.fld_itemid) === String(itemid) ? { ...r, updateTransferQty: value } : r
    ));
    if (isNaN(qty) || qty === 0) {
      toast.warning("Transfer Qty should not be Zero");
      setEditRows(prev => prev.map(r =>
        String(r.fld_itemid) === String(itemid) ? { ...r, updateTransferQty: "" } : r
      ));
      return;
    }
    const available = parseFloat(rowData.fld_closing_stock || 0);
    const requested = parseFloat(rowData.fld_Qty || 0);
    if (requested > 0 && qty > requested) {
      toast.warning("Please give a Proper input");
      setEditRows(prev => prev.map(r =>
        String(r.fld_itemid) === String(itemid) ? { ...r, updateTransferQty: "" } : r
      ));
      return;
    }
    if (qty > available) {
      toast.warning("Please give a Proper input");
      setEditRows(prev => prev.map(r =>
        String(r.fld_itemid) === String(itemid) ? { ...r, updateTransferQty: "" } : r
      ));
    }
  }, []);

  // ──────────────────────────────────────────────
  // Select All checkbox handlers
  // ASP: SelectAllRights('chkSelect', this.checked)
  // ──────────────────────────────────────────────
  const handleSelectAllOutward = useCallback((checked) => {
    setSelectAllOutward(checked);
    setOutwardRows(prev => prev.map(r => ({ ...r, isSelected: checked })));
  }, []);

  const handleSelectAllApprove = useCallback((checked) => {
    setSelectAllApprove(checked);
    setEditRows(prev => prev.map(r => ({ ...r, isApproveChecked: checked })));
  }, []);

  // ──────────────────────────────────────────────
  // Serial No entry popup
  // ASP: gv_outwardlist_RowCommand("serialentry")
  //   → opens SerialNoEntry.aspx as popup
  // ──────────────────────────────────────────────
  const handleSerialNoClick = useCallback((row) => {
    const transferQty = parseFloat(row.transferQty || 0);
    if (!row.transferQty || transferQty <= 0) {
      toast.warning("Please enter Transfer qty and try again");
      return;
    }
    if (String(row.transferQty).includes(".")) {
      toast.warning("Serial no entry not allowed for decimal transfer quantity");
      return;
    }
    if (transferQty <= 0) {
      toast.warning("Serial no entry not allowed for Transfer quantity 0");
      return;
    }
    const dateOutward = currentDate ? currentDate.replace(/-/g, "/") : "";
    const url = `/Inventory/SerialNoEntry?item=${row.fld_itemid}-${fromStoreId}-${branchId}-${dateOutward}&qty=${transferQty}&pro=STNO&f=1`;
    window.open(url, "_blank", "width=800,height=800,scrollbars=yes,menubar=no,resizable=no,directories=no,location=no");
  }, [currentDate, fromStoreId, branchId]);

  // ──────────────────────────────────────────────
  // Validate before submit
  // ASP: fieldvalidation() + validate() + ConfirmSubmit()
  // ──────────────────────────────────────────────
  const validateSubmit = useCallback(() => {
    if (fromStoreId === "Select") { toast.warning("Please select From store field"); return false; }
    if (toBranchId  === "Select") { toast.warning("Please select Transfer Branch Field"); return false; }
    if (toStoreId   === "Select") { toast.warning("Please select To store field"); return false; }
    if (!remarks.trim())          { toast.warning("Please Enter Remarks"); return false; }
    if (approveUserId === "Select") { toast.warning("Pls Select Approve User"); return false; }
    const selectedRows = outwardRows.filter(r => r.isSelected);
    if (selectedRows.length === 0) { toast.warning("Please select any One"); return false; }
    for (const row of selectedRows) {
      const qty = parseFloat(row.transferQty || 0);
      if (qty <= 0) { toast.warning(`Transfer Qty should not be 0 for ${row.fld_itemcode}`); return false; }
      const avail = parseFloat(row.fld_closing_stock || 0);
      if (qty > avail) { toast.warning(`Transfer Qty should be less than OR equal to Available QTY for ${row.fld_itemcode}`); return false; }
      // Serial no check
      if (row.fld_IsSerialNo && row.serialno === "Yes") {
        const snoData = serialNoMap[String(row.fld_itemid)];
        if (!snoData || snoData.length === 0) {
          toast.warning(`Please select serial no for ${row.fld_itemcode}`);
          return false;
        }
        if (snoData.length !== qty) {
          toast.warning(`Transfer Qty and Serial No Miss-matches for ${row.fld_itemcode}`);
          return false;
        }
      }
    }
    return true;
  }, [fromStoreId, toBranchId, toStoreId, remarks, approveUserId, outwardRows, serialNoMap]);

  // ──────────────────────────────────────────────
  // SUBMIT (New Entry)
  // ASP: gv_outwardlist_RowCommand("SUBMIT")
  //   → InsertSTNOutward_Direct_WOStockUpdate
  //   → retry up to 10x on duplicate outward no (returnValue=3)
  //   → on success (returnValue=6): SMS popup + redirect to STNListings
  // ──────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!validateSubmit()) return;
    setSubmitting(true);
    const selectedRows = outwardRows.filter(r => r.isSelected);
    const snoItems = [];
    selectedRows.forEach(row => {
      const snoData = serialNoMap[String(row.fld_itemid)];
      if (snoData) snoItems.push(...snoData);
    });
    const payload = {
      branchId,
      toBranchId,
      fromStoreId,
      currentDate: isoToDDMM(currentDate),
      remarks,
      loginUserId,
      username,
      approveUserId,
      items: selectedRows.map(r => ({
        fld_itemid:        r.fld_itemid,
        fld_branchitemid:  r.fld_branchitemid,
        fld_closing_stock: r.fld_closing_stock,
        transferQty:       r.transferQty,
        fld_IsSerialNo:    r.fld_IsSerialNo,
        serialno:          r.serialno,
      })),
      snoItems,
    };
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-outward-direct/submit`, payload);
      const rv = res.data?.returnValue;
      switch (rv) {
        case 2:
          toast.error("Please refresh and try again");
          break;
        case 3:
          toast.error("Transaction not saved. Please refresh and try again");
          break;
        case 4:
          toast.error("STN Item(s) current Quantity changed. Please refresh and retry");
          break;
        case 5:
          toast.error("Transaction not saved. Please refresh and try again");
          break;
        case 6: {
          const branchwiseno = res.data.branchwiseno;
          const billnoanddate = `Direct STNO No: ${branchwiseno}- Outwarded,${isoToDDMM(currentDate)}`;
          const smsUrl = `/SMS/SendSMS?STNINo=${encodeURIComponent(billnoanddate)}`;
          window.open(smsUrl, "_blank", "width=400,height=150,scrollbars=yes,menubar=no,resizable=no,directories=no,location=no");
          toast.success("STN Outward saved successfully");
          // Clear serial no session data
          setSerialNoMap({});
          setTimeout(() => navigate("/Inventory/STNListings"), 1200);
          break;
        }
        default:
          toast.error("Unexpected error. Please try again.");
      }
    } catch (e) {
      toast.error("Submit failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  }, [validateSubmit, outwardRows, branchId, toBranchId, fromStoreId, currentDate,
      remarks, loginUserId, username, approveUserId, serialNoMap, navigate]);

  // ──────────────────────────────────────────────
  // EDIT SUBMIT (Update existing STN)
  // ASP: gv_outlistedit_RowCommand("SUBMIT")
  //   → UpdateSTNOutward_Direct_WOStockUpdate
  // ──────────────────────────────────────────────
  const handleEditSubmit = useCallback(async () => {
    if (!remarks.trim()) { toast.warning("Please Enter Remarks"); return; }
    if (approveUserId === "Select") { toast.warning("Pls Select Approve User"); return; }
    setSubmitting(true);
    const payload = {
      branchId,
      toBranchId,
      fromStoreId,
      currentDate: isoToDDMM(currentDate),
      remarks,
      loginUserId,
      approveUserId,
      stnOutwardId: sessionStnOutwardId,
    };
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-outward-direct/edit-submit`, payload);
      const rv = res.data?.returnValue;
      if (rv === 6) {
        const billnoanddate = `Direct STNO No: ${sessionStnOutwardNo}- Updated,${isoToDDMM(currentDate)}`;
        const smsUrl = `/SMS/SendSMS?STNINo=${encodeURIComponent(billnoanddate)}`;
        window.open(smsUrl, "_blank", "width=400,height=150,scrollbars=yes,menubar=no,resizable=no,directories=no,location=no");
        toast.success("STN Outward saved successfully");
        setTimeout(() => navigate("/Inventory/STNListings"), 1200);
      } else {
        toast.error("Transaction not saved. Please refresh and try again");
      }
    } catch (e) {
      toast.error("Edit submit failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  }, [remarks, approveUserId, branchId, toBranchId, fromStoreId, currentDate,
      loginUserId, sessionStnOutwardId, sessionStnOutwardNo, navigate]);

  // ──────────────────────────────────────────────
  // APPROVE
  // ASP: gv_outlistedit_RowCommand("Approve")
  //   → update tbl_stn_outward_mas + tbl_stn_outward_det per checked item
  //   → redirect to STNOuwardApprove.aspx
  // ──────────────────────────────────────────────
  const handleApprove = useCallback(async () => {
    const checkedItems = editRows.filter(r => r.isApproveChecked);
    if (checkedItems.length === 0) { toast.warning("Please select items to approve"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-outward-direct/approve`, {
        stnOutwardId: sessionStnOutwardId,
        username,
        items: checkedItems.map(r => ({ fld_itemid: r.fld_itemid })),
      });
      if (res.data?.success) {
        toast.success("Approved successfully");
        setTimeout(() => navigate("/Inventory/STNOutwardApprove"), 1000);
      } else {
        toast.error("Approval failed");
      }
    } catch (e) {
      toast.error("Approve failed: " + e.message);
    } finally {
      setSubmitting(false);
    }
  }, [editRows, sessionStnOutwardId, username, navigate]);

  // ──────────────────────────────────────────────
  // Check approve column visibility
  // ASP: if (Session["LoginUserid"] == "37" || Session["LoginUserid"] == approveperson)
  // ──────────────────────────────────────────────
  const showApproveColumn = useCallback(() => {
    const approvePersonId = sessionStorage.getItem("stnApprovePersonId") || "";
    return loginUserId === "37" || loginUserId === approvePersonId;
  }, [loginUserId]);

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="STN Outward - Direct Entry" />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ══════════════════════════════════════════════
            HEADER FORM CARD
        ══════════════════════════════════════════════ */}
        <div className={`${cardBase} mb-6`}>
          {/* Card Header */}
          <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
            <h2 className="text-sm font-bold text-white tracking-wider uppercase">
              STN Outward - Direct Entry
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-white/70 text-xs font-medium">INFRA SCREEN : A-34/1</span>
              <button
                onClick={() => navigate("/Inventory/STNListings")}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            {/* Row 1 — STN Outward No + From Store */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>STN Outward No</label>
                <div className={`mt-1 px-3 py-2 rounded-xl border text-sm font-bold text-purple-600 ${isDark?"border-white/10 bg-gray-800/60":"border-gray-200 bg-gray-50"}`}>
                  {stnOutwardNo || <span className="text-gray-400 font-normal">Auto Generated</span>}
                </div>
              </div>
              <div>
                <label className={labelBase}>From Store</label>
                {/* ASP: auto-filled, disabled */}
                <select
                  value={fromStoreId}
                  onChange={e => !isEditMode && handleFromStoreChange(e.target.value)}
                  disabled={true}
                  className={`${inputDisabled} mt-1`}
                >
                  <option value="Select">Select</option>
                  {fromStores.map(s => (
                    <option key={s.value || s.fld_storeid} value={s.value || s.fld_storeid}>
                      {s.label || s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2 — To Branch + Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>To Branch</label>
                <select
                  value={toBranchId}
                  onChange={e => handleToBranchChange(e.target.value)}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`}
                >
                  <option value="Select">Select</option>
                  {toBranches.map(b => (
                    <option key={b.fld_Branchid || b.value} value={b.fld_Branchid || b.value}>
                      {b.fld_BranchName || b.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>Date</label>
                <input
                  type="date"
                  value={currentDate}
                  onChange={e => handleDateChange(e.target.value)}
                  disabled={isEditMode}
                  max={getTodayISO()}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`}
                />
              </div>
            </div>

            {/* Row 3 — To Store + Remarks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>To Store</label>
                <select
                  value={toStoreId}
                  onChange={e => setToStoreId(e.target.value)}
                  disabled={isEditMode}
                  className={`${isEditMode ? inputDisabled : inputBase} mt-1`}
                >
                  <option value="Select">Select</option>
                  {toStores.map(s => (
                    <option key={s.value || s.fld_storeid} value={s.value || s.fld_storeid}>
                      {s.label || s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelBase}>Remarks <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={remarks}
                  onChange={e => setRemarks(e.target.value)}
                  placeholder="Enter remarks"
                  className={`${inputBase} mt-1`}
                />
              </div>
            </div>

            {/* Row 4 — Approve User */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-start-2">
                <label className={labelBase}>Approve User <span className="text-red-500">*</span></label>
                <select
                  value={approveUserId}
                  onChange={e => setApproveUserId(e.target.value)}
                  className={`${inputBase} mt-1`}
                >
                  <option value="Select">Select</option>
                  {approveUsers.map(u => (
                    <option key={u.value || u.fld_userid} value={u.value || u.fld_userid}>
                      {u.label || u.fld_username}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            FILTRATION AREA (new entry only)
            ASP: div_filteratio — hidden in edit mode
        ══════════════════════════════════════════════ */}
        {showFilterArea && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Filtration Area</h3>
            </div>
            <div className="p-5 space-y-4">
              {/* Row 1 — Item Category + Item Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>Item Category</label>
                  <select
                    value={itemCategoryId}
                    onChange={e => setItemCategoryId(e.target.value)}
                    className={`${inputBase} mt-1`}
                  >
                    <option value="Select">Select</option>
                    {itemCategories.map(c => (
                      <option key={c.value || c.fld_itemcategoryid} value={c.value || c.fld_itemcategoryid}>
                        {c.label || c.fld_itemcategoryname}
                      </option>
                    ))}
                  </select>
                </div>
                <div ref={acCodeRef}>
                  <label className={labelBase}>Item Code</label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={itemCode}
                      onChange={e => { setItemCode(e.target.value); fetchAutoComplete("code", e.target.value); }}
                      placeholder="Type to search..."
                      className={inputBase}
                    />
                    {showAcCode && acCodeSuggestions.length > 0 && (
                      <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto ${isDark?"bg-gray-800 border-white/10":"bg-white border-gray-200"}`}>
                        {acCodeSuggestions.map((s, i) => (
                          <div key={i} onClick={() => { setItemCode(s); setShowAcCode(false); }}
                            className={`px-3 py-2 text-sm cursor-pointer ${isDark?"hover:bg-gray-700 text-gray-200":"hover:bg-blue-50 text-gray-800"}`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2 — Item Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div ref={acNameRef}>
                  <label className={labelBase}>Item Name</label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={itemName}
                      onChange={e => { setItemName(e.target.value); fetchAutoComplete("name", e.target.value); }}
                      placeholder="Type to search..."
                      className={inputBase}
                    />
                    {showAcName && acNameSuggestions.length > 0 && (
                      <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto ${isDark?"bg-gray-800 border-white/10":"bg-white border-gray-200"}`}>
                        {acNameSuggestions.map((s, i) => (
                          <div key={i} onClick={() => { setItemName(s); setShowAcName(false); }}
                            className={`px-3 py-2 text-sm cursor-pointer ${isDark?"hover:bg-gray-700 text-gray-200":"hover:bg-blue-50 text-gray-800"}`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex justify-center pt-1">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}
                >
                  {loading ? <RefreshCcw size={15} className="animate-spin"/> : <Search size={15}/>}
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {/* ═════════════════════════════
                GRID 1: Item Search Results
                ASP: gv_itemmaster
            ═════════════════════════════ */}
            {itemSearchRows.length > 0 && (
              <div className="px-5 pb-5">
                <div className="overflow-x-auto rounded-xl">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={`${thBase} text-center`}>S.No</th>
                        <th className={thBase}>Item Code</th>
                        <th className={thBase}>Item Name</th>
                        <th className={thBase}>UOM</th>
                        <th className={`${thBase} text-center`}>
                          {/* Select All */}
                          <input type="checkbox"
                            onChange={e => setItemSearchRows(prev => prev.map(r => ({ ...r, isSelected: e.target.checked })))}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemSearchPaged.map((row, idx) => {
                        const globalIdx = (itemSearchPage - 1) * ITEM_PAGE_SIZE + idx;
                        return (
                          <tr key={row.itemid || idx}
                            className={`border-t transition ${isDark?"border-white/5":"border-gray-100"} ${globalIdx%2===0?(isDark?"bg-gray-800/20":"bg-gray-50/60"):""}  hover:bg-blue-50/30`}>
                            <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                            <td className={tdBase}>{row.itemcode}</td>
                            <td className={tdBase}>{row.itemname}</td>
                            <td className={tdBase}>{row.itemuom}</td>
                            <td className={`${tdBase} text-center`}>
                              <input type="checkbox" checked={!!row.isSelected}
                                onChange={e => setItemSearchRows(prev => prev.map(r => r.itemid === row.itemid ? { ...r, isSelected: e.target.checked } : r))}
                                className="w-4 h-4 cursor-pointer"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="px-3 py-2 text-center">
                          <button
                            onClick={handleAddItems}
                            className={`flex items-center gap-2 mx-auto ${primaryBtnClass} ${primaryHoverClass} text-white px-5 py-2 rounded-xl text-xs font-semibold transition`}
                          >
                            <PlusCircle size={14}/> Add Selected Items
                          </button>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {itemSearchTotalPages > 1 && (
                  <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2 ${isDark?"border-white/10 text-gray-400":"border-gray-100 text-gray-500"}`}>
                    <span>Showing {Math.min((itemSearchPage-1)*ITEM_PAGE_SIZE+1, itemSearchRows.length)}–{Math.min(itemSearchPage*ITEM_PAGE_SIZE, itemSearchRows.length)} of {itemSearchRows.length}</span>
                    <Pagination page={itemSearchPage} totalPages={itemSearchTotalPages} onPage={p => setItemSearchPage(p)}/>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════
            FUTURE DATE WARNING
            ASP: lbl_mess + lst_items
        ══════════════════════════════════════════════ */}
        {futureDateItems.length > 0 && (
          <div className={`${cardBase} mb-6 p-4`}>
            <p className="text-purple-600 font-bold text-sm mb-2">
              Items Are Already outwarded/Issued on Future Date So Cant Issue On Previous Date
            </p>
            <div className={`rounded-xl border p-3 max-h-28 overflow-y-auto text-xs ${isDark?"border-white/10 bg-gray-800/40":"border-gray-200 bg-gray-50"}`}>
              {futureDateItems.map((item, i) => (
                <div key={i} className="py-1 border-b border-dashed last:border-0">
                  {item.itemcode_name} &nbsp;||&nbsp; Issued Date: {item.fld_documentdate}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            GRID 2: Outward List (New Entry)
            ASP: gv_outwardlist — shown when items are added
        ══════════════════════════════════════════════ */}
        {outwardRows.length > 0 && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Outward Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>Part No</th>
                    <th className={thBase}>UOM</th>
                    <th className={`${thBase} text-center`}>Available Stock</th>
                    <th className={`${thBase} text-center`}>Qty as on Date</th>
                    <th className={`${thBase} text-center`}>Transfer Qty</th>
                    <th className={`${thBase} text-center`}>Serial No</th>
                    <th className={`${thBase} text-center`}>
                      <div className="flex items-center gap-1 justify-center">
                        <input type="checkbox" checked={selectAllOutward}
                          onChange={e => handleSelectAllOutward(e.target.checked)}
                          className="w-4 h-4 cursor-pointer"/>
                        <span>Select All</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {outwardPaged.map((row, idx) => {
                    const globalIdx = (outwardPage - 1) * OUTWARD_PAGE_SIZE + idx;
                    return (
                      <tr key={row.fld_itemid || idx}
                        className={`border-t transition ${isDark?"border-white/5":"border-gray-100"} ${globalIdx%2===0?(isDark?"bg-gray-800/20":"bg-gray-50/60"):""} hover:bg-blue-50/30`}>
                        <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                        <td className={tdBase}>{row.fld_itemcode}</td>
                        <td className={tdBase}>{row.fld_itemname}</td>
                        <td className={tdBase}>{row.fld_itemPartNo || "—"}</td>
                        <td className={tdBase}>{row.fld_uomname}</td>
                        <td className={`${tdBase} text-center`}>{row.fld_closing_stock}</td>
                        <td className={`${tdBase} text-center`}>{row.datewiseqty}</td>
                        <td className={`${tdBase} text-center`}>
                          <input
                            type="text"
                            value={row.transferQty || ""}
                            maxLength={9}
                            onChange={e => {
                              const v = e.target.value.replace(/[^0-9.]/g, "");
                              setOutwardRows(prev => prev.map(r =>
                                String(r.fld_itemid)===String(row.fld_itemid) ? {...r, transferQty: v} : r
                              ));
                            }}
                            onBlur={e => handleTransferQtyChange(row.fld_itemid, e.target.value, row)}
                            className={`w-20 px-2 py-1 rounded-lg border text-xs text-center outline-none focus:ring-2 focus:ring-blue-400 ${isDark?"bg-gray-700 border-white/10 text-white":"bg-white border-gray-300 text-gray-900"}`}
                          />
                        </td>
                        <td className={`${tdBase} text-center`}>
                          {row.fld_IsSerialNo ? (
                            <button
                              onClick={() => handleSerialNoClick(row)}
                              className="text-blue-500 hover:text-blue-700 underline text-xs font-semibold"
                            >
                              {row.serialno || "Enter"}
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </td>
                        <td className={`${tdBase} text-center`}>
                          <input type="checkbox" checked={!!row.isSelected}
                            onChange={e => setOutwardRows(prev => prev.map(r =>
                              String(r.fld_itemid)===String(row.fld_itemid) ? {...r, isSelected: e.target.checked} : r
                            ))}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={10} className="px-3 py-3 text-center">
                      <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className={`flex items-center gap-2 mx-auto ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}
                      >
                        {submitting ? <RefreshCcw size={15} className="animate-spin"/> : <Send size={15}/>}
                        {submitting ? "Submitting..." : "Submit"}
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {outwardTotalPages > 1 && (
              <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2 ${isDark?"border-white/10 text-gray-400":"border-gray-100 text-gray-500"}`}>
                <span>Showing {Math.min((outwardPage-1)*OUTWARD_PAGE_SIZE+1, outwardRows.length)}–{Math.min(outwardPage*OUTWARD_PAGE_SIZE, outwardRows.length)} of {outwardRows.length}</span>
                <Pagination page={outwardPage} totalPages={outwardTotalPages} onPage={p => setOutwardPage(p)}/>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════
            GRID 3: Edit / Approve Grid
            ASP: gv_outlistedit — shown in edit mode
        ══════════════════════════════════════════════ */}
        {showEditGrid && editRows.length > 0 && (
          <div className={`${cardBase} mb-6`}>
            <div className={`px-5 py-3 ${primaryBtnClass}`}>
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Existing STN Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={`${thBase} text-center`}>S.No</th>
                    <th className={thBase}>Item Code</th>
                    <th className={thBase}>Item Name</th>
                    <th className={thBase}>UOM</th>
                    <th className={`${thBase} text-center`}>Available Stock</th>
                    <th className={`${thBase} text-center`}>Transfer Qty</th>
                    <th className={`${thBase} text-center`}>FIFO Prices</th>
                    <th className={`${thBase} text-center`}>Total Price</th>
                    {showApproveColumn() && (
                      <th className={`${thBase} text-center`}>
                        <div className="flex items-center gap-1 justify-center">
                          <input type="checkbox" checked={selectAllApprove}
                            onChange={e => handleSelectAllApprove(e.target.checked)}
                            className="w-4 h-4 cursor-pointer"/>
                          <span>Approve</span>
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {editPaged.map((row, idx) => {
                    const globalIdx = (editPage - 1) * EDIT_PAGE_SIZE + idx;
                    return (
                      <tr key={row.fld_itemid || idx}
                        className={`border-t transition ${isDark?"border-white/5":"border-gray-100"} ${globalIdx%2===0?(isDark?"bg-gray-800/20":"bg-gray-50/60"):""} hover:bg-blue-50/30`}>
                        <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                        <td className={tdBase}>{row.fld_itemcode}</td>
                        <td className={tdBase}>{row.fld_itemname}</td>
                        <td className={tdBase}>{row.fld_uomname}</td>
                        <td className={`${tdBase} text-center`}>{row.fld_closing_stock}</td>
                        <td className={`${tdBase} text-center`}>
                          {/* ASP: txt_updatetransferqty — Enabled=false in edit mode */}
                          <input
                            type="text"
                            value={row.updateTransferQty || ""}
                            disabled={!editEnabled}
                            onChange={e => handleEditQtyChange(row.fld_itemid, e.target.value, row)}
                            className={`w-20 px-2 py-1 rounded-lg border text-xs text-center outline-none ${!editEnabled?"opacity-60 cursor-not-allowed":""} focus:ring-2 focus:ring-blue-400 ${isDark?"bg-gray-700 border-white/10 text-white":"bg-white border-gray-300 text-gray-900"}`}
                          />
                        </td>
                        <td className={`${tdBase} text-center`}>{row.fld_fifoprices || "—"}</td>
                        <td className={`${tdBase} text-center`}>{row.TotalPriceCur || row.TotalPrice || "0"}</td>
                        {showApproveColumn() && (
                          <td className={`${tdBase} text-center`}>
                            <input type="checkbox"
                              checked={!!row.isApproveChecked}
                              onChange={e => setEditRows(prev => prev.map(r =>
                                String(r.fld_itemid)===String(row.fld_itemid) ? {...r, isApproveChecked: e.target.checked} : r
                              ))}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  {editEnabled && (
                    <tr>
                      <td colSpan={showApproveColumn() ? 7 : 6} className="px-3 py-2 text-right text-xs font-bold">
                        Grand Total:
                      </td>
                      <td className={`${tdBase} text-center font-bold`}>{grandTotal}</td>
                      {showApproveColumn() && (
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={handleApprove}
                            disabled={submitting}
                            className={`flex items-center gap-1 mx-auto ${primaryBtnClass} ${primaryHoverClass} text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition`}
                          >
                            <Check size={13}/> Approve
                          </button>
                        </td>
                      )}
                    </tr>
                  )}
                  {editEnabled && (
                    <tr>
                      <td colSpan={showApproveColumn() ? 9 : 8} className="px-3 py-3 text-center">
                        <button
                          onClick={handleEditSubmit}
                          disabled={submitting}
                          className={`flex items-center gap-2 mx-auto ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}
                        >
                          {submitting ? <RefreshCcw size={15} className="animate-spin"/> : <Send size={15}/>}
                          {submitting ? "Saving..." : "Submit"}
                        </button>
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
            {editTotalPages > 1 && (
              <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2 ${isDark?"border-white/10 text-gray-400":"border-gray-100 text-gray-500"}`}>
                <span>Showing {Math.min((editPage-1)*EDIT_PAGE_SIZE+1, editRows.length)}–{Math.min(editPage*EDIT_PAGE_SIZE, editRows.length)} of {editRows.length}</span>
                <Pagination page={editPage} totalPages={editTotalPages} onPage={p => setEditPage(p)}/>
              </div>
            )}
          </div>
        )}

      </div>
    </LayoutWrapper>
  );
}